import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { Calendar } from './Calendar';

/**
 * Day view — single day selection.
 *
 * @param {number[]} [daysWithData]  Day-of-month numbers (1–31) that have
 *   available data. When provided, a small accent dot is rendered beneath the
 *   day number. Only applies to days within the viewed month.
 *   Example: [1, 5, 12, 25]
 */
export function DayPicker({ selectedDate, onSelect, maxDate, daysWithData = [], onViewMonthChange, monthsWithData = [], yearsWithData = [] }) {
    const today = dayjs();

    const renderDay = (day, viewMonth, { tabIndex }) => {
        const isSelected = day.isSame(selectedDate, 'day');
        const isToday = day.isSame(today, 'day');
        const isOutside = !day.isSame(viewMonth, 'month');
        const isDisabled = maxDate ? day.isAfter(maxDate, 'day') : false;
        const isHighlighted =
            !isOutside && daysWithData.includes(day.format('YYYY-MM-DD'));

        return (
            <div key={day.toString()} role="gridcell" aria-selected={isSelected} className="flex items-center justify-center">
                <button
                    type="button"
                    tabIndex={tabIndex}
                    onClick={() => onSelect(day)}
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    aria-current={isToday ? 'date' : undefined}
                    aria-label={day.format('dddd, MMMM D, YYYY')}
                    data-date={day.format('YYYY-MM-DD')}
                    className={cn(
                        'relative flex h-9 w-9 flex-col items-center justify-center rounded-full text-sm transition-colors',
                        'hover:bg-accent/20 hover:text-foreground',
                        isSelected && 'bg-accent text-accent-foreground hover:bg-accent',
                        isToday && !isSelected && 'bg-muted text-foreground',
                        isOutside && 'text-muted-foreground opacity-50',
                        isDisabled && 'pointer-events-none opacity-30'
                    )}
                >
                    <span className={isHighlighted ? '-translate-y-px' : ''}>
                        {day.date()}
                    </span>
                    {isHighlighted && (
                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute bottom-1 h-1 w-1 rounded-full',
                                isSelected ? 'bg-accent-foreground' : 'bg-accent'
                            )}
                        />
                    )}
                </button>
            </div>
        );
    };

    return (
        <Calendar
            selectedDate={selectedDate}
            renderDay={renderDay}
            maxDate={maxDate}
            onViewMonthChange={onViewMonthChange}
            monthsWithData={monthsWithData}
            yearsWithData={yearsWithData}
        />
    );
}
